import React, { useState, useEffect, useRef } from "react";
import styles from "./style.module.scss";
import { useSelector } from "react-redux";
import { RootState } from "../../../../state";

interface LoopSliderProps {
  name: string;
  sLapValue: string | number;
  renameCorner?: (oldName: string, newName: string) => void;
  changeCornerValue?: (cornerName: string, newValue: string | number) => void;
  reorderCornerConfig?: () => void;
  deleteCorner?: (name: string) => void;
  showSlider?: boolean;
}

function LoopSlider({
  name = "",
  sLapValue = 100,
  renameCorner,
  changeCornerValue,
  reorderCornerConfig,
  deleteCorner,
  showSlider = true,
}: LoopSliderProps) {
  const configLapData = useSelector(
    (state: RootState) => state.virtualLoops.configLapData,
  );

  const [localName, setLocalName] = useState(name);
  const [localSLapValue, setLocalSLapValue] = useState(sLapValue);

  useEffect(() => {
    setLocalName(name);
  }, [name]);

  useEffect(() => {
    setLocalSLapValue(sLapValue);
  }, [sLapValue]);

  const min = 100;
  const max =
    configLapData && "sLap" in configLapData && configLapData.sLap
      ? Math.max(...configLapData.sLap) - 100
      : 100;

  const numericSLapValue =
    sLapValue === "EOL"
      ? max
      : typeof sLapValue === "string"
      ? parseFloat(sLapValue) || 0
      : sLapValue;

  const fillPercent = ((numericSLapValue - min) / (max - min)) * 100;

  const pendingReorderRef = useRef(false);

  useEffect(() => {
    if (pendingReorderRef.current) {
      reorderCornerConfig?.();
      pendingReorderRef.current = false;
    }
  }, [sLapValue, reorderCornerConfig]);

  return (
    <div
      className={`${styles.container} ${
        sLapValue === "EOL" ? styles.noDelete : ""
      }`}
    >
      <input
        className={styles.nameInput}
        type="text"
        value={localName}
        onChange={(e) => setLocalName(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === "Enter") {
            renameCorner?.(name, localName);
          }
        }}
        onBlur={() => renameCorner?.(name, localName)}
      />
      {showSlider && (
        <input
          className={styles.rangeInput}
          type="range"
          min={min}
          max={max}
          value={numericSLapValue}
          onChange={(e) =>
            changeCornerValue?.(name, parseFloat(e.target.value))
          }
          onMouseUp={reorderCornerConfig}
          onTouchEnd={reorderCornerConfig}
          disabled={sLapValue === "EOL"}
          style={{ "--fill-percent": `${fillPercent}%` } as React.CSSProperties}
        />
      )}
      {sLapValue === "EOL" ? (
        <span className={styles.sLapInput}>EOL</span>
      ) : (
        <input
          className={styles.sLapInput}
          type="number"
          value={localSLapValue}
          onChange={(e) => setLocalSLapValue(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.currentTarget.blur();
            }
          }}
          onBlur={(e) => {
            changeCornerValue?.(name, Number(e.target.value));
            pendingReorderRef.current = true;
          }}
          readOnly={false}
        />
      )}
      {sLapValue !== "EOL" ? (
        <button
          className={styles.deleteButton}
          onClick={() => deleteCorner?.(name)}
        >
          -
        </button>
      ) : (
        <div className={styles.deleteButton}></div>
      )}
    </div>
  );
}

export default LoopSlider;
